import React from 'react';
import { MapPin, Building, AlertTriangle } from 'lucide-react';

/**
 * VerticalBadge — mostra Lands/Launches/Ambas/Sem vínculo com cores e estados.
 *
 * Props:
 * - vertical: 'lands' | 'launches' | 'both' | null
 *     null/undefined → renderiza "Sem vínculo" em cinza
 * - completeness: 'complete' | 'partial' | 'no_employment' (default 'complete')
 *     'partial' → mostra triângulo de alerta com tooltip
 * - isHistorical: boolean (default false) — vínculo não-atual → opacity 50%
 * - missingFields: string[] — usado no tooltip quando partial
 * - size: 'sm' | 'md' (default 'sm')
 *
 * D-09 cobertura parcial explícita: UI não finge saber o que não sabe.
 *
 * Cores: business_units.ui_color (Lands #3b82f6, Launches #8b5cf6)
 * Ícones: business_units.ui_icon (map-pin → MapPin, building → Building)
 */
export function VerticalBadge({
  vertical,
  completeness = 'complete',
  isHistorical = false,
  missingFields = [],
  size = 'sm',
}) {
  // Caso 'both' → 2 badges lado a lado
  if (vertical === 'both') {
    return (
      <div className="inline-flex gap-1">
        <VerticalBadge vertical="lands" completeness={completeness} isHistorical={isHistorical} missingFields={missingFields} size={size} />
        <VerticalBadge vertical="launches" completeness={completeness} isHistorical={isHistorical} missingFields={missingFields} size={size} />
      </div>
    );
  }

  // Caso null/no_employment → "Sem vínculo" cinza
  if (!vertical || completeness === 'no_employment') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded ${size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} bg-slate-100 text-slate-500 ${isHistorical ? 'opacity-50' : ''}`}
        title="Sem vínculo atual"
      >
        Sem vínculo
      </span>
    );
  }

  // Lands ou Launches
  const config = {
    lands:    { label: 'Lands',    bg: 'bg-blue-50',   text: 'text-blue-700',   Icon: MapPin },
    launches: { label: 'Launches', bg: 'bg-violet-50', text: 'text-violet-700', Icon: Building },
  }[vertical];

  if (!config) return null; // fallback seguro

  const sizeClass = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  const histClass = isHistorical ? 'opacity-50' : '';

  const tooltipParts = [`Vertical: ${config.label}`];
  if (completeness === 'partial' && missingFields.length > 0) {
    tooltipParts.push(`Faltam: ${missingFields.join(', ')}`);
  }
  if (isHistorical) tooltipParts.push('(vínculo histórico)');

  return (
    <span
      className={`inline-flex items-center gap-1 rounded ${sizeClass} ${config.bg} ${config.text} font-medium ${histClass}`}
      title={tooltipParts.join(' · ')}
    >
      <config.Icon size={size === 'sm' ? 10 : 12} />
      {config.label}
      {completeness === 'partial' && (
        <AlertTriangle size={size === 'sm' ? 9 : 11} className="text-amber-500" />
      )}
    </span>
  );
}

export default VerticalBadge;
